import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { Field } from "../components/Layout";
import { FieldBar } from "./FieldApp";
import { LocalChainage, currentPosition, gpsError, label } from "./geo";
import { newId, put, storageUse, type ControlItem, type GpsItem, type MediaItem } from "./queue";
import { mb, useSync } from "./useSync";

interface SurveyInfo {
  survey: { survey_uuid: string; road_name: string; route_number: string; road_class: string; agency: string; county: string; status: string; start_location_name: string; end_location_name: string; notes: string | null };
  stats: { gps: number; chainage_m: number | null; frames: number };
}

const CACHE_KEY = (id: string) => `rai-field-survey-${id}`;
function cached(id: string): SurveyInfo | null {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY(id)) || "null"); } catch { return null; }
}

export function Recorder() {
  const { surveyId = "" } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState<SurveyInfo | null>(() => cached(surveyId));
  const [loadError, setLoadError] = useState<string | null>(null);

  const [recording, setRecording] = useState(false);
  const [gpsEvery, setGpsEvery] = useState(1000);
  const [frameEvery, setFrameEvery] = useState(5000);
  const [withVideo, setWithVideo] = useState(false);
  const [fix, setFix] = useState<{ lat: number; lon: number; acc: number; kph: number | null } | null>(null);
  const [chain, setChain] = useState<{ m: number; source: "server" | "phone" }>({ m: 0, source: "server" });
  const [counts, setCounts] = useState({ gps: 0, frames: 0 });
  const [msg, setMsg] = useState<{ text: string; tone?: "ok" | "bad" } | null>(null);
  const [ending, setEnding] = useState(false);
  const [endName, setEndName] = useState("");
  const [ended, setEnded] = useState(false);

  const videoEl = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const watch = useRef<number | null>(null);
  const frameTimer = useRef<number | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const session = useRef<string | null>(null);
  const chunkSeq = useRef(0);
  const lastSent = useRef(0);
  const local = useRef(new LocalChainage(0));
  const lastFix = useRef<GeolocationPosition | null>(null);
  const wake = useRef<WakeLockSentinel | null>(null);

  const onGps = useCallback((latest: Record<string, unknown> | null) => {
    if (latest && typeof latest.chainage_m === "number") setChain({ m: latest.chainage_m, source: "server" });
  }, []);
  const sync = useSync(surveyId, onGps);

  useEffect(() => {
    api<SurveyInfo>(`/api/field/surveys/${surveyId}`)
      .then((d) => {
        setInfo(d);
        setEndName(d.survey.end_location_name || "");
        local.current = new LocalChainage(d.stats.chainage_m ?? 0);
        setChain({ m: d.stats.chainage_m ?? 0, source: "server" });
        setEnded(d.survey.status !== "ACTIVE");
        try { localStorage.setItem(CACHE_KEY(surveyId), JSON.stringify(d)); } catch { /* storage full or blocked */ }
      })
      .catch((e) => { if (!cached(surveyId)) setLoadError(e instanceof Error ? e.message : "Survey not found."); });
  }, [surveyId]);

  /* -------------------------------------------------- GPS */
  const onPosition = useCallback(async (p: GeolocationPosition) => {
    lastFix.current = p;
    setFix({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy, kph: p.coords.speed == null ? null : p.coords.speed * 3.6 });
    const now = Date.now();
    if (now - lastSent.current < gpsEvery) return;
    lastSent.current = now;
    const id = newId(), at = new Date(p.timestamp || now).toISOString();
    const item: GpsItem = {
      id, survey_uuid: surveyId, at,
      payload: { client_event_uuid: id, survey_uuid: surveyId, lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy,
                 altitude: p.coords.altitude, speed: p.coords.speed, heading: p.coords.heading, captured_at_device: at, offline_captured: !navigator.onLine },
    };
    await put("gps", item);
    setCounts((c) => ({ ...c, gps: c.gps + 1 }));
    const m = local.current.add(p.coords.latitude, p.coords.longitude, p.timestamp || now);
    if (!navigator.onLine) setChain({ m, source: "phone" });
  }, [gpsEvery, surveyId]);

  /* -------------------------------------------------- camera */
  const captureFrame = useCallback(async () => {
    const v = videoEl.current;
    if (!v || !v.videoWidth) return;
    const w = Math.min(v.videoWidth, 1280), h = Math.round(v.videoHeight * (w / v.videoWidth));
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d")!.drawImage(v, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, "image/jpeg", 0.8));
    if (!blob) return;
    const id = newId(), at = new Date().toISOString();
    const item: MediaItem = {
      id, survey_uuid: surveyId, at, blob, filename: `${id}.jpg`,
      meta: { survey_uuid: surveyId, client_event_uuid: id, captured_at_device: at, session_uuid: session.current ?? "", offline_captured: String(!navigator.onLine) },
    };
    await put("frames", item);
    setCounts((x) => ({ ...x, frames: x.frames + 1 }));
  }, [surveyId]);

  const queueControl = async (url: string, body: Record<string, unknown>, phase: ControlItem["phase"]) => {
    const id = newId();
    await put("controls", { id, survey_uuid: surveyId, url, body: { client_event_uuid: id, offline_captured: !navigator.onLine, ...body }, at: new Date().toISOString(), phase });
  };

  const startVideo = () => {
    if (!stream.current || !("MediaRecorder" in window)) return;
    const type = MediaRecorder.isTypeSupported("video/webm;codecs=vp8") ? "video/webm;codecs=vp8" : "video/webm";
    const rec = new MediaRecorder(stream.current, { mimeType: type, videoBitsPerSecond: 1_200_000 });
    rec.ondataavailable = async (e) => {
      if (!e.data?.size) return;
      const s = await storageUse();
      if (s.quota && s.usage / s.quota > 0.85) { setMsg({ text: "Phone storage is over 85% full, so video is no longer saved. GPS and images continue.", tone: "bad" }); return; }
      chunkSeq.current += 1;
      const id = newId(), at = new Date().toISOString();
      await put("videos", {
        id, survey_uuid: surveyId, at, blob: e.data, filename: `${id}.webm`,
        meta: { survey_uuid: surveyId, client_event_uuid: id, session_uuid: session.current ?? "", chunk_sequence: String(chunkSeq.current), captured_at_device: at, offline_captured: String(!navigator.onLine) },
      } satisfies MediaItem);
    };
    rec.start(30_000);
    recorder.current = rec;
  };

  const start = async () => {
    setMsg(null);
    if (!navigator.geolocation) { setMsg({ text: "This browser can't read GPS.", tone: "bad" }); return; }
    watch.current = navigator.geolocation.watchPosition(onPosition, (e) => setMsg({ text: gpsError(e), tone: "bad" }), { enableHighAccuracy: true, maximumAge: 250, timeout: 15000 });
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
      if (videoEl.current) videoEl.current.srcObject = stream.current;
      session.current = newId();
      const v = stream.current.getVideoTracks()[0]?.getSettings();
      await queueControl(`/api/field/surveys/${surveyId}/video-session`, { action: "start", session_uuid: session.current, at_device: new Date().toISOString(), sample_interval_ms: frameEvery, camera_width: v?.width, camera_height: v?.height }, "start");
      frameTimer.current = window.setInterval(captureFrame, frameEvery);
      if (withVideo) startVideo();
    } catch {
      setMsg({ text: "The camera didn't start. GPS is still recording. Allow camera access for this site and tap Start again to add images.", tone: "bad" });
    }
    try { wake.current = await navigator.wakeLock?.request("screen"); } catch { /* not supported or refused */ }
    setRecording(true);
  };

  const stop = useCallback(async () => {
    if (watch.current !== null) navigator.geolocation.clearWatch(watch.current);
    watch.current = null;
    if (frameTimer.current) window.clearInterval(frameTimer.current);
    frameTimer.current = null;
    if (recorder.current && recorder.current.state !== "inactive") {
      await new Promise<void>((r) => { recorder.current!.addEventListener("stop", () => r(), { once: true }); recorder.current!.stop(); });
    }
    recorder.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (session.current) {
      await queueControl(`/api/field/surveys/${surveyId}/video-session`, { action: "stop", session_uuid: session.current, at_device: new Date().toISOString() }, "stop");
      session.current = null;
    }
    try { await wake.current?.release(); } catch { /* already released */ }
    setRecording(false);
  }, [surveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { stop(); }, [stop]);
  useEffect(() => { if (params.get("autostart") === "1" && info && !recording && !ended) start(); }, [info]); // eslint-disable-line react-hooks/exhaustive-deps

  // While recording and online, push the queue every 5 seconds in batches.
  useEffect(() => {
    if (!recording) return;
    const t = window.setInterval(() => { if (navigator.onLine) sync.sync(); }, 5000);
    return () => window.clearInterval(t);
  }, [recording, sync.sync]); // eslint-disable-line react-hooks/exhaustive-deps

  const endSurvey = async () => {
    setMsg({ text: "Saving the end point…" });
    let p: GeolocationPosition | null = lastFix.current;
    try { p = await currentPosition(12000); } catch { /* use the last fix */ }
    if (p) await onPosition(p);
    await stop();
    await queueControl(`/api/field/surveys/${surveyId}/end`, {
      end_location_name: endName.trim(), ended_at_device: new Date().toISOString(),
      lat: p?.coords.latitude ?? null, lon: p?.coords.longitude ?? null, accuracy: p?.coords.accuracy ?? null,
    }, "stop");
    setEnding(false);
    setEnded(true);
    await sync.sync();
    setMsg(navigator.onLine ? { text: "Survey ended and saved.", tone: "ok" } : { text: "Survey ended offline. Keep this phone's browser data until it has synced.", tone: "ok" });
  };

  if (loadError && !info) {
    return (
      <div className="field-page"><FieldBar title="Survey" back="/field" />
        <main className="field-main"><p className="field-msg bad">{loadError}</p><Link className="btn field-btn" to="/field">Back to surveys</Link></main>
      </div>
    );
  }
  const s = info?.survey;
  const pilotTarget = Number(/Pilot drive, target (\d+(?:\.\d+)?) km/i.exec(s?.notes ?? "")?.[1]) || null;

  return (
    <div className="field-page">
      <FieldBar title={s?.road_name ?? "Survey"} back="/field" />
      <main className="field-main">
        <p className="muted" style={{ fontSize: 13 }}>{[s?.road_class && `Class ${s.road_class}`, s?.route_number, s?.agency, s?.county].filter(Boolean).join(" · ")}</p>

        <section className="field-hero" aria-live="polite">
          <div className="field-chain"><span>Chainage</span><b>{label(chain.m)}</b><small>{chain.source === "server" ? "from server" : "provisional, from phone GPS"}</small></div>
          <div className="field-stats">
            <div><span>GPS</span><b>{fix ? `±${Math.round(fix.acc)} m` : recording ? "Waiting…" : "Off"}</b></div>
            <div><span>Speed</span><b>{fix?.kph != null ? `${fix.kph.toFixed(0)} km/h` : "—"}</b></div>
            <div><span>Images</span><b>{counts.frames}</b></div>
            <div><span>Not synced</span><b className={sync.pending.total ? "warn" : ""}>{sync.pending.total}</b></div>
          </div>
          {pilotTarget ? <div className="field-pilot"><span>Pilot drive: {(chain.m / 1000).toFixed(2)} of {pilotTarget} km</span><progress max={pilotTarget * 1000} value={Math.min(chain.m, pilotTarget * 1000)} aria-label="Pilot drive progress" />{chain.m >= pilotTarget * 1000 ? <b>Target reached. You can end the survey.</b> : null}</div> : null}
          <p className={`field-net ${sync.online ? "ok" : "bad"}`}>{sync.online ? "Online · syncing automatically" : "Offline · everything is saved on this phone"}{sync.storage.quota ? ` · ${mb(sync.storage.usage)} used` : ""}</p>
        </section>

        <video ref={videoEl} className="field-video" autoPlay playsInline muted hidden={!recording} />

        {msg ? <p className={`field-msg ${msg.tone ?? ""}`} role={msg.tone === "bad" ? "alert" : "status"}>{msg.text}</p> : null}
        {sync.last?.error ? <p className="field-msg bad" role="alert">{sync.last.error.message}{sync.last.error.kind === "signed_out" ? <> <Link to={`/sign-in?next=/field/${surveyId}`}>Sign in</Link></> : null}</p> : null}

        {ended ? (
          <section className="field-card stack">
            <h2 className="field-h">Survey ended</h2>
            <p className="muted">{sync.pending.total ? `${sync.pending.total} items still to sync. Keep the app open on a connection, or come back to it later.` : "Everything is on the server. Images are being checked by AI and blurred for privacy."}</p>
            <Link className="btn btn-good field-btn" to="/field/start">＋ Start another survey</Link>
            <Link className="btn field-btn" to="/field">Your surveys</Link>
          </section>
        ) : !recording ? (
          <section className="field-card stack">
            <div className="field-two">
              <Field label="GPS every" htmlFor="rc-gps"><select id="rc-gps" className="select field-input" value={gpsEvery} onChange={(e) => setGpsEvery(Number(e.target.value))}><option value={1000}>1 second</option><option value={2000}>2 seconds</option><option value={5000}>5 seconds</option></select></Field>
              <Field label="Image every" htmlFor="rc-img"><select id="rc-img" className="select field-input" value={frameEvery} onChange={(e) => setFrameEvery(Number(e.target.value))}><option value={2000}>2 seconds</option><option value={5000}>5 seconds</option><option value={10000}>10 seconds</option></select></Field>
            </div>
            <label className="check"><input type="checkbox" checked={withVideo} onChange={(e) => setWithVideo(e.target.checked)} />Also save audit video (uses a lot of phone storage)</label>
            <button className="btn btn-good field-btn field-cta" onClick={start}>Start recording</button>
          </section>
        ) : (
          <div className="stack">
            <button className="btn field-btn" onClick={captureFrame}>Take an image now</button>
            <button className="btn field-btn" onClick={stop}>Pause recording</button>
          </div>
        )}

        {!ended ? (
          ending ? (
            <section className="field-card stack field-end">
              <h2 className="field-h">End this survey?</h2>
              <p className="muted">GPS and camera stop and the end point is saved. You can't add to this survey afterwards.</p>
              <Field label="Ends at (landmark)" htmlFor="rc-end"><input id="rc-end" className="input field-input" value={endName} onChange={(e) => setEndName(e.target.value)} /></Field>
              <button className="btn btn-danger field-btn" onClick={endSurvey}>End survey</button>
              <button className="btn field-btn" onClick={() => setEnding(false)}>Keep recording</button>
            </section>
          ) : (
            <div className="field-bottom"><button className="btn btn-danger field-btn" onClick={() => setEnding(true)}>End survey</button></div>
          )
        ) : null}
      </main>
    </div>
  );
}
